// @flow
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { FieldProps, FormProps } from "redux-form";
import { Field, formValues } from "redux-form";
import { Form, Select } from "semantic-ui-react";
import { isTablet } from "Commons/Utilities/isMobile";

import FormLabel from "Commons/Components/FormComponents/components/FormLabelWithDivider";
import { mapLocations } from "../../../../Commons/Components/FormComponents/misc/Mappers";
import { isPostalCode, isPresent } from "../../../../Commons/Utilities";
import { isPostalCode as isPostalCodeValidator } from "../../../../Commons/Utilities/validators";
import { fetchLocations } from "../../../../redux/actions/locations";
import { getLocations } from "../../../../redux/sagas/selectors";
import { InputField } from "../../ChangeServices/submodules/Old_Edit/components/Fields";
import { isRequired } from "../../ChangeServices/submodules/Old_Edit/misc/Validators";
import type { ChangeServiceT } from "../../misc/Types";

type LocationPropsT = {|
  initialValues: ChangeServiceT,
  fields: Dict,
  postalcode: string,
  locationId: string,
  category: string,
  form: string,
  change: $PropertyType<FormProps, "change">,
  width?: number,
  width2?: number,
  noLabel?: boolean,
  t: typeof TFunction,
  divider: boolean,
  onChange?: Function,
|};

export const PostalCodeAndLocation = ({
  fields,
  t,
  initialValues,
  category,
  form,
  change,
  noLabel,
  width = 3,
  width2 = 7,
  divider,
}: LocationPropsT) => {
  return (
    <Form.Group>
      {!noLabel && (
        <FormLabel
          divider={divider}
          id={fields.postalCode}
          title={t("common:postalCodeAndLocation")}
          inline
          tooltip={t("mobile.customer.addressDetails.addressInfo")}
          secondary
        />
      )}
      <Field
        name={fields.postalCode}
        validate={[isRequired, isPostalCodeValidator]}
        component={InputField}
        props={{
          id: fields.postalCode,
          placeholder: t("common:postalCode"),
          width: isTablet() ? 6 : width,
          showErrorMessage: false,
          onChange: (value) => {
            if (isPostalCode(value) !== undefined) {
              change("location", null);
              change("locationId", null);
              change("locationMainName", null);
              change("locationPartName", null);
              change("street", null);
            }
          },
        }}
        style={{ marginBottom: "1rem" }}
      />
      <Location
        fields={fields}
        initialValues={initialValues}
        category={category}
        form={form}
        width={isTablet() ? 10 : width2}
        change={change}
      />
    </Form.Group>
  );
};

let LocationField = (props: LocationPropsT) => {
  const { initialValues = {}, fields, postalcode, locationId, category, form, change, width, onChange } = props;
  const { t } = useTranslation("common");
  const dispatch = useDispatch();

  useEffect(() => {
    if (isPostalCode(postalcode)) {
      dispatch(fetchLocations(postalcode, category, form, fields.postalCode));
    }
  }, [postalcode, category, dispatch, form, fields.postalCode]);

  const locations = useSelector((state) => {
    const currentLocations = getLocations(state, category, postalcode);
    const defaultOption = [
      {
        name: initialValues.location,
        id: initialValues.locationId || -1,
        mainName: initialValues.location,
        partName: initialValues.location,
      },
    ];

    return currentLocations ? currentLocations : defaultOption;
  });

  const isLoading = useSelector(({ locations }) => locations.isFetching);

  useEffect(() => {
    // Autofill location selector if there is only one location.
    if (
      postalcode &&
      postalcode.length === 5 &&
      postalcode !== initialValues.postalcode &&
      locations[0].id !== -1 &&
      locations.length === 1 &&
      locations[0].id !== locationId
    ) {
      change(fields.locationId, locations[0].id);
      change(fields.locationName, locations[0].name);
      change(fields.locationMainName, locations[0].mainName);
      change(fields.locationPartName, locations[0].partName);

      if (onChange) {
        onChange();
      }
    }
  }, [locations, postalcode, locationId, fields, initialValues.postalcode, change, onChange]);

  const options = locations.map(mapLocations);

  const disabled = isPresent(postalcode) && options[0].value !== -1 && options.length === 1;

  return (
    <Form.Field width={width}>
      <Field
        name={fields.locationId}
        validate={[isRequired]}
        component={(props: FieldProps) => {
          return (
            <Select
              lazyLoad
              selection
              search
              loading={isLoading}
              icon={options && (options.length > 1 || isLoading) ? undefined : null}
              className={disabled ? "profile-input city" : "location-dropdown"}
              searchInput={{
                id: "location",
                type: "search",
                autoComplete: "off",
              }}
              placeholder={t("locationExample")}
              disabled={disabled}
              options={options}
              value={props.input.value}
              error={props.meta.invalid && props.meta.touched}
              onChange={(event, data) => {
                props.input.onChange(data.value);
                const location = data.options.find((option) => option.value === data.value);
                change(fields.locationName, location.text);
                change(fields.locationMainName, location.mainname);
                change(fields.locationPartName, location.partname);
                change(fields.street, null);

                if (onChange) {
                  onChange();
                }
              }}
            />
          );
        }}
      />
    </Form.Field>
  );
};

export const Location = formValues((props) => ({
  postalcode: props.fields.postalCode,
  locationId: props.fields.locationId,
}))(LocationField);
